<template>
  <v-dialog
    :model-value="modelValue"
    class="bq-status-dialog-root"
    content-class="bq-status-dialog-root"
    max-width="480"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <SnackbarComp :SnackbarComponent="snackbar" />

    <v-card rounded="xl" class="user-dialog-card bq-status-dialog">
      <div class="dialog-header bq-status-dialog__header">
        <div class="bq-status-dialog__header-main">
          <div
            class="dialog-header__icon bq-status-dialog__icon-wrap"
            :class="toneClass"
            aria-hidden="true"
          >
            <v-icon :color="iconColor" size="20">{{ copy.icon }}</v-icon>
          </div>
          <div class="bq-status-dialog__head-copy">
            <span class="dialog-header__title">{{ copy.title }}</span>
            <span class="bq-status-dialog__subtitle">{{ copy.subtitle }}</span>
          </div>
        </div>
        <v-btn
          icon
          variant="flat"
          size="small"
          class="dialog-close-btn bq-status-dialog__close"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="dialog-body bq-status-dialog__body">
        <p class="bq-status-dialog__lead">
          {{ copy.confirmLead }}
          <strong>{{ leadName }}</strong>?
        </p>
        <div class="bq-status-dialog__target">
          <p class="bq-status-dialog__name">{{ leadName }}</p>
          <p class="bq-status-dialog__meta">
            {{ lead?.email && lead.email !== '—' ? lead.email : 'No email on file' }}
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="dialog-footer bq-status-dialog__footer">
        <v-spacer />
        <v-btn
          variant="flat"
          class="text-none dialog-btn bq-status-dialog__cancel"
          rounded="lg"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          variant="flat"
          class="text-none dialog-btn bq-status-dialog__confirm"
          :class="confirmButtonClass"
          rounded="lg"
          :loading="saving"
          @click="submit"
        >
          {{ copy.confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref } from 'vue'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'
import '@/styles/business-queries/status-dialog.css'

defineOptions({ name: 'LeadConfirmDialog' })

const ACTION_COPY = {
  enable: {
    icon: 'mdi-play-circle-outline',
    title: 'Enable lead',
    subtitle: 'Make this lead active again.',
    confirmLead: 'Enable',
    confirmLabel: 'Enable lead',
    errorFallback: 'Failed to enable lead',
    successText: 'Lead enabled successfully.',
    tone: 'converted',
    color: 'success',
  },
  disable: {
    icon: 'mdi-pause-circle-outline',
    title: 'Disable lead',
    subtitle: 'Mark this lead as inactive.',
    confirmLead: 'Disable',
    confirmLabel: 'Disable lead',
    errorFallback: 'Failed to disable lead',
    successText: 'Lead disabled successfully.',
    tone: 'pending',
    color: 'grey',
  },
  delete: {
    icon: 'mdi-trash-can-outline',
    title: 'Delete lead',
    subtitle: 'This action cannot be undone.',
    confirmLead: 'Permanently delete',
    confirmLabel: 'Delete lead',
    errorFallback: 'Failed to delete lead',
    successText: 'Lead deleted successfully.',
    tone: 'rejected',
    color: 'error',
  },
}

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  action: {
    type: String,
    default: 'delete',
    validator: (value) => ['enable', 'disable', 'delete'].includes(value),
  },
  lead: { type: Object, default: null },
  confirmFn: { type: Function, required: true },
})

const emit = defineEmits(['update:modelValue', 'confirmed'])

const saving = ref(false)
const snackbar = ref({})

const leadName = computed(() => {
  const name = String(props.lead?.name || '').trim()
  return !name || name === '—' ? 'this lead' : name
})

const copy = computed(() => ACTION_COPY[props.action] || ACTION_COPY.delete)
const toneClass = computed(() => `bq-status-dialog__icon-wrap--${copy.value.tone}`)
const iconColor = computed(() => copy.value.color)
const confirmButtonClass = computed(() => {
  if (props.action === 'delete') return 'bq-status-dialog__confirm--error'
  if (props.action === 'disable') return 'bq-status-dialog__confirm--neutral'
  return 'bq-status-dialog__confirm--primary'
})

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  saving.value = true
  try {
    const result = await props.confirmFn(props.lead)
    if (!result?.ok) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: result?.error || copy.value.errorFallback,
      }
      return
    }

    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'success',
      SnackbarText: result.message || copy.value.successText,
    }
    emit('confirmed')
    close()
  } catch (error) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: getApiMessage(error, 'Request failed'),
    }
  } finally {
    saving.value = false
  }
}
</script>
